import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { queryClient } from "../../App";
import { changeTheme, logout } from "../../app/slices/userSlice";
import { get, post } from "../../services/api";
import {
  buildTenantRelativePath,
  clearStoredTenantPreviewSlug,
  getTenantPreviewSlug,
} from "../../utils/tenantPreview";
import { useBranding } from "../../hooks/useBranding";
import styles from "./ClanWorkspaceShell.module.css";

const DEFAULT_PLATFORM_LOGO_URL = "/uniflo-reference/logo.png";

const ReferenceBrandMark = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" className={styles.referenceBrandMark}>
    <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="4.4" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
  </svg>
);

const ReferenceNavIcon = ({ name }) => {
  const s = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  const glyphs = {
    home: <g><path {...s} d="M3.5 11 12 4l8.5 7" /><path {...s} d="M5.5 9.7V20h13V9.7" /><path {...s} d="M10 20v-4.5h4V20" /></g>,
    search: <g><circle {...s} cx="11" cy="11" r="6" /><path {...s} d="m20 20-3.6-3.6" /></g>,
    goals: <g><circle {...s} cx="12" cy="12" r="8" /><circle {...s} cx="12" cy="12" r="4.4" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /></g>,
    roles: <path {...s} d="M12 3.5 19 6v5c0 4.6-3 7.7-7 9.5-4-1.8-7-4.9-7-9.5V6z" />,
    subclans: <g><circle {...s} cx="6" cy="12" r="2.3" /><circle {...s} cx="17.5" cy="6.5" r="2.3" /><circle {...s} cx="17.5" cy="17.5" r="2.3" /><path {...s} d="M8.2 11 15.3 7.4M8.2 13l7.1 3.6" /></g>,
    bonds: <path {...s} d="M8.2 9c-1.9 0-3.1 1.4-3.1 3s1.2 3 3.1 3c2.4 0 3.3-3 3.8-3s1.4 3 3.8 3c1.9 0 3.1-1.4 3.1-3s-1.2-3-3.1-3c-2.4 0-3.3 3-3.8 3s-1.4-3-3.8-3Z" />,
    messages: <g><path {...s} d="M5.2 6.5h13.6v8.2a3 3 0 0 1-3 3H10l-4.8 3V6.5Z" /><path {...s} d="M8.5 10h7M8.5 13h4.8" /></g>,
    calendar: <g><path {...s} d="M5 6.5h14v12.2H5z" /><path {...s} d="M8 4.4v4M16 4.4v4M5 10.2h14" /><path {...s} d="M8.2 13.5h2.1M13.6 13.5h2.1M8.2 16.2h2.1" /></g>,
    alerts: <g><path {...s} d="M6.6 10.5a5.4 5.4 0 0 1 10.8 0v3.1l1.7 3H4.9l1.7-3v-3.1Z" /><path {...s} d="M9.7 19a2.6 2.6 0 0 0 4.6 0" /></g>,
    connections: <g><circle {...s} cx="8" cy="8" r="2.6" /><circle {...s} cx="16" cy="16" r="2.6" /><path {...s} d="M10 10 14 14M14 7.5h3.5V11M6.5 13v3.5H10" /></g>,
    notes: <g><path {...s} d="M6 4.8h9.4L18 7.4v11.8H6z" /><path {...s} d="M15 4.8V8h3M9 11.5h6M9 14.5h6M9 17.5h3.8" /></g>,
    commerce: <g><path {...s} d="M4.5 7.5h15v10h-15z" /><path {...s} d="M4.5 10.2h15" /><path {...s} d="M8 15.2h3.8" /><path {...s} d="M16.5 14.2a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8Z" /></g>,
    flows: <g><rect {...s} x="9" y="3.6" width="6" height="4.6" rx="1.4" /><rect {...s} x="3.4" y="15.8" width="6" height="4.6" rx="1.4" /><rect {...s} x="14.6" y="15.8" width="6" height="4.6" rx="1.4" /><path {...s} d="M12 8.2v3.4M6.4 15.8v-2.2h11.2v2.2" /></g>,
    reports: <g><path {...s} d="M4 20h16" /><path {...s} d="M7.5 20v-5.5M12 20V8.5M16.5 20v-8" /></g>,
  };
  return <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">{glyphs[name] || glyphs.home}</svg>;
};

const BrandLogo = ({ logoUrl, brandName = "Uniflo", shellVariant = "default" }) => {
  const trimmedLogoUrl = `${logoUrl || ""}`.trim();
  const [referenceMarkFailed, setReferenceMarkFailed] = React.useState(false);

  // Reference (clan-redesign) shell: render the real platform branding logo
  // when configured, falling back to the concentric reference mark when absent
  // or when the image fails to load.
  if (shellVariant === "clan-redesign") {
    if (!trimmedLogoUrl || referenceMarkFailed) {
      return <ReferenceBrandMark />;
    }
    return (
      <img
        src={trimmedLogoUrl}
        alt={brandName}
        className={styles.referenceBrandImg}
        onError={() => setReferenceMarkFailed(true)}
      />
    );
  }

  const resolvedLogoUrl = trimmedLogoUrl || DEFAULT_PLATFORM_LOGO_URL;
  return (
    <img
      src={resolvedLogoUrl}
      alt={brandName}
      className={styles.brandLogo}
      onError={(event) => {
        if (event.currentTarget.dataset.fallbackApplied === "true") {
          return;
        }
        event.currentTarget.dataset.fallbackApplied = "true";
        event.currentTarget.src = DEFAULT_PLATFORM_LOGO_URL;
      }}
    />
  );
};

const IconHome = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M12 4.2 4.5 10v9h5.2v-5.4h4.6V19h5.2v-9L12 4.2Z" />
  </svg>
);

const IconSearch = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="m21 20.3-4.4-4.4a7.6 7.6 0 1 0-.7.7l4.4 4.4.7-.7ZM10.4 17a6.6 6.6 0 1 1 0-13.2 6.6 6.6 0 0 1 0 13.2Z" />
  </svg>
);

const IconTarget = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <circle cx="12" cy="12" r="7.2" stroke="currentColor" strokeWidth="1.9" />
    <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.9" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
  </svg>
);

const IconRoles = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path
      d="M12 3.3 5 6.3v4.9c0 4 2.8 7.7 7 8.9 4.2-1.2 7-4.9 7-8.9V6.3l-7-3Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10.1" r="2.1" fill="currentColor" />
  </svg>
);

const IconSubclans = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <circle cx="7" cy="7" r="2.3" fill="currentColor" />
    <circle cx="17" cy="7" r="2.3" fill="currentColor" />
    <circle cx="12" cy="17" r="2.3" fill="currentColor" />
    <path d="M7 9.8v2.4h10V9.8M12 12.2v2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconFlows = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M6 6h4v4H6zM14 14h4v4h-4zM14 4h4v4h-4z" stroke="currentColor" strokeWidth="1.8" />
    <path d="M10 8h4M16 8v6M10 8v8h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconBonds = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path
      d="M8.1 9.2a2.7 2.7 0 0 1 0-3.8l1.3-1.3a2.7 2.7 0 0 1 3.8 0 2.7 2.7 0 0 1 0 3.8l-1 1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.9 14.8a2.7 2.7 0 0 1 0 3.8l-1.3 1.3a2.7 2.7 0 0 1-3.8 0 2.7 2.7 0 0 1 0-3.8l1-1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M9.4 14.6 14.6 9.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconMessages = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M5.5 6.5h13v8.2a3 3 0 0 1-3 3H10l-4.5 3V6.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M8.6 10h6.8M8.6 13h4.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M5 6.5h14V19H5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M8 4.5v4M16 4.5v4M5 10h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M8.2 13.5h2.2M13.6 13.5h2.2M8.2 16.2h2.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconAlerts = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M6.5 10.5a5.5 5.5 0 0 1 11 0v3.2l1.7 3H4.8l1.7-3v-3.2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M9.7 19a2.6 2.6 0 0 0 4.6 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconConnections = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <circle cx="8" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="16" cy="16" r="2.6" stroke="currentColor" strokeWidth="1.8" />
    <path d="M10 10 14 14M14 7.5h3.5V11M6.5 13v3.5H10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconNotes = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M6 4.8h9.4L18 7.4v11.8H6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M15 4.8V8h3M9 11.5h6M9 14.5h6M9 17.5h3.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconCommerce = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M4.5 7.5h15v10h-15z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M4.5 10.2h15M8 15.2h3.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="16.5" cy="15.6" r="1.35" fill="currentColor" />
  </svg>
);

const IconReports = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M5 19V9M12 19V5M19 19v-8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    <path d="M4 19.5h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconSettings = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path
      d="M12 8.3a3.7 3.7 0 1 0 0 7.4 3.7 3.7 0 0 0 0-7.4Zm8 4.2-1.7.6c-.1.4-.3.8-.5 1.2l.8 1.6-1.8 1.8-1.6-.8c-.4.2-.8.4-1.2.5l-.6 1.7h-2.6l-.6-1.7c-.4-.1-.8-.3-1.2-.5l-1.6.8-1.8-1.8.8-1.6c-.2-.4-.4-.8-.5-1.2L4 12.5V10l1.7-.6c.1-.4.3-.8.5-1.2L5.4 6.6l1.8-1.8 1.6.8c.4-.2.8-.4 1.2-.5l.6-1.7h2.6l.6 1.7c.4.1.8.3 1.2.5l1.6-.8 1.8 1.8-.8 1.6c.2.4.4.8.5 1.2L20 10v2.5Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

const IconWorkspace = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M4.5 5.5h15v13h-15z" stroke="currentColor" strokeWidth="1.8" />
    <path d="M4.5 10.5h15M10 5.5v13" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const IconUsage = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M5 18V9.5M11.5 18V5.5M18 18v-6.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    <path d="M4 18.5h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconSupport = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M6 7.5A2.5 2.5 0 0 1 8.5 5h7A2.5 2.5 0 0 1 18 7.5v6A2.5 2.5 0 0 1 15.5 16H10l-4 3v-11.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const IconOpenExternal = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none">
    <path d="M8 7h9v9M17 7l-10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconLogout = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M10 6.5H6v11h4M14 8l4 4-4 4M18 12H9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconMoon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path
      d="M19 14.2A7.6 7.6 0 0 1 9.8 5c-3.2.9-5.5 3.8-5.5 7.2A7.5 7.5 0 0 0 19 14.2Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const railItems = [
  { key: "home", label: "Home", to: "/", icon: IconHome, end: true },
  { key: "search", label: "Search", to: "/search", icon: IconSearch },
  { key: "goals", label: "Goals", to: "/goals", icon: IconTarget },
  { key: "roles", label: "Roles", to: "/roles", icon: IconRoles },
  { key: "subclans", label: "Subclans", to: "/subclans", icon: IconSubclans },
  { key: "bonds", label: "Bonds", to: "/bonds", icon: IconBonds },
  { key: "commerce", label: "Commerce", to: "/commerce", icon: IconCommerce },
  { key: "flows", label: "Flows", to: "/flows", icon: IconFlows },
  { key: "reports", label: "Reports", to: "/reports", icon: IconReports },
];

const normalizeRailPathname = (path = "") => {
  const normalized = `${path || ""}`.split(/[?#]/)[0].replace(/\/+$/, "");
  return normalized || "/";
};

export default function ClanWorkspaceShell({
  user,
  secondaryLabel = "Workspace panel",
  secondaryContent = null,
  secondaryCollapsed = false,
  // Opt-in only — every other caller keeps the default "glass" translucent
  // panel look. Pass "solid" to render the secondary rail as a flat white
  // card instead (used by the Clan Admin shell, which needs to pixel-match
  // a prototype panel that has no blur/translucency). Never flip the
  // default, that would restyle every other clan surface's secondary rail.
  secondaryVariant = "glass",
  // Opt-in only — overrides the secondary rail's width via the
  // `--secondary-width` custom property. Leave unset to keep the existing
  // 480px default for every other caller.
  secondaryWidth = "",
  testId = "clan-workspace-shell",
  shellVariant = "default",
  subclanOnly = false,
  subclanHomePath = "",
  subclanName = "",
  // T-062 S2 — true while the subclan record has not arrived yet. The rail
  // and every label that names the workspace then say so, instead of falling
  // back to the parent clan's name and confidently naming the wrong place.
  subclanPending = false,
  subclanId = "",
  tenantSlug = "",
  children,
  ...shellProps
}) {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state) => state.user.theme || "light");
  const navigate = useNavigate();
  const location = useLocation();
  const profileMenuRef = useRef(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profilePhotoErrored, setProfilePhotoErrored] = useState(false);
  const tenantPreviewSlug = getTenantPreviewSlug();
  const activeTenantSlug = `${tenantSlug || tenantPreviewSlug || ""}`.trim();
  const tenantPath = useMemo(
    () => (path) => buildTenantRelativePath(path, activeTenantSlug),
    [activeTenantSlug]
  );
  const { platformLogoUrl, platformSiteName } = useBranding();
  const workspaceSiteName = shellProps.siteName;
  const platformBrandName = platformSiteName || "Uniflo";
  const isReferenceShell = shellVariant === "clan-redesign";
  const authIdentity =
    `${user?._id || user?.guid || user?.username || user?.email || user?.mobile || "anon"}`.trim() ||
    "anon";

  const { data: shellBootstrap } = useQuery({
    queryKey: ["workspace-shell-bootstrap", activeTenantSlug, subclanId, authIdentity],
    queryFn: async () => {
      const response = await get(
        subclanId
          ? `/api/v1/users/bootstrap?subclanId=${encodeURIComponent(subclanId)}`
          : "/api/v1/users/bootstrap"
      );
      return response.data || {};
    },
    enabled: Boolean(user && (!subclanOnly || subclanId)),
    staleTime: 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const bootstrapUser = shellBootstrap?.user || {};
  const userFirstName = `${user?.first_name || user?.firstName || bootstrapUser?.first_name || bootstrapUser?.firstName || ""}`.trim();
  const userMiddleName = `${user?.middle_name || user?.middleName || bootstrapUser?.middle_name || bootstrapUser?.middleName || ""}`.trim();
  const userLastName = `${user?.last_name || user?.lastName || bootstrapUser?.last_name || bootstrapUser?.lastName || ""}`.trim();
  const userProfilePhoto = `${user?.photo || user?.avatar || bootstrapUser?.photo || bootstrapUser?.avatar || ""}`.trim();

  const displayNameLines = useMemo(() => {
    const lines = [userFirstName, userMiddleName, userLastName].filter(Boolean);
    if (lines.length) return lines;
    const fallback = `${user?.username || bootstrapUser?.username || "Uniflo User"}`.trim();
    return fallback ? [fallback] : ["Uniflo User"];
  }, [bootstrapUser?.username, user?.username, userFirstName, userLastName, userMiddleName]);

  const displayName = useMemo(() => {
    return displayNameLines.join(" ").trim();
  }, [displayNameLines]);
  const profileInitial = useMemo(() => {
    const source =
      userFirstName ||
      userMiddleName ||
      userLastName ||
      `${user?.username || bootstrapUser?.username || ""}`.trim() ||
      "U";
    return source.slice(0, 1).toUpperCase();
  }, [bootstrapUser?.username, user?.username, userFirstName, userLastName, userMiddleName]);

  useEffect(() => {
    setProfilePhotoErrored(false);
  }, [userProfilePhoto]);

  const currentTenantMembership = useMemo(() => {
    const memberships = Array.isArray(shellBootstrap?.tenantMemberships)
      ? shellBootstrap.tenantMemberships
      : [];
    const currentTenant = shellBootstrap?.currentTenant || {};
    const currentTenantId = `${currentTenant?._id || currentTenant?.id || ""}`.trim();
    const currentTenantSlug = `${currentTenant?.slug || ""}`.trim();
    return (
      memberships.find((item) => {
        const tenant = item?.tenant || {};
        return (
          (currentTenantId && `${tenant?._id || tenant?.id || ""}` === currentTenantId) ||
          (currentTenantSlug && tenant?.slug === currentTenantSlug)
        );
      }) ||
      memberships.find((item) => {
        const tenant = item?.tenant || {};
        return activeTenantSlug && tenant?.slug === activeTenantSlug;
      }) || null
    );
  }, [activeTenantSlug, shellBootstrap?.currentTenant, shellBootstrap?.tenantMemberships]);

  // T-062 S2 — inside a subclan this is the SUBCLAN's name, or the neutral
  // word "Subclan" while the record loads. It must never fall back to
  // `workspaceSiteName` (the parent clan's name): that is what made the rail,
  // the profile menu and the support link read as the clan's for the first
  // second or so after a hard load of a subclan URL.
  const subclanWorkspaceLabel = useMemo(() => {
    const resolved = `${subclanName || ""}`.trim();
    if (resolved) return resolved;
    return subclanPending ? "Loading subclan" : "Subclan";
  }, [subclanName, subclanPending]);
  const currentClanName = useMemo(() => {
    if (subclanOnly) return subclanWorkspaceLabel;
    return (
      `${currentTenantMembership?.tenant?.name || workspaceSiteName || "Workspace"}`.trim() ||
      "Workspace"
    );
  }, [currentTenantMembership?.tenant?.name, subclanOnly, subclanWorkspaceLabel, workspaceSiteName]);
  const isSubclanWorkspaceRoute = useMemo(() => {
    return location.pathname.includes("/subclan/");
  }, [location.pathname]);

  const membershipRole = `${currentTenantMembership?.role || ""}`.trim().toLowerCase();
  const hasTenantWideRailAccess = useMemo(() => {
    return (
      membershipRole === "tenant_owner" ||
      membershipRole === "tenant_admin" ||
      shellBootstrap?.platform?.hasPlatformAccess === true
    );
  }, [membershipRole, shellBootstrap?.platform?.hasPlatformAccess]);

  const profileRoleLabel = useMemo(() => {
    if (membershipRole === "tenant_owner") return "Founder";
    if (membershipRole === "tenant_admin") return "Clan Principal";
    if (membershipRole === "moderator") return "Moderator";
    if (membershipRole === "member") return "Member";
    return "Member";
  }, [membershipRole]);

  const canOpenAdministration = useMemo(() => {
    if (subclanOnly) return false;
    return hasTenantWideRailAccess;
  }, [hasTenantWideRailAccess, subclanOnly]);

  const profileDialogPrimaryHref = subclanOnly
    ? tenantPath("/account")
    : canOpenAdministration
    ? tenantPath("/administrator")
    : tenantPath("/account");
  const profileDialogPrimaryLabel = subclanOnly
    ? "Account Settings"
    : canOpenAdministration
    ? `${currentClanName} Admin`
    : "Account Settings";
  const profileDialogSupportHref = tenantPath("/support");
  const profileDialogSupportLabel = `${currentClanName} Support`;
  const buildSubclanRailPath = useMemo(() => {
    const basePath = `${subclanHomePath || ""}`.trim() || tenantPath("/");
    const baseWithoutSlash = basePath.replace(/\/+$/, "");

    return (path = "/") => {
      const normalizedPath = `${path || "/"}`.trim();
      if (!normalizedPath || normalizedPath === "/") {
        return basePath;
      }
      const suffix = normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
      return `${baseWithoutSlash}${suffix}`;
    };
  }, [subclanHomePath, tenantPath]);
  const subclanRailItems = useMemo(
    () => [
      {
        // T-062 S2 — the rail's own loading state: a subclan whose name has
        // not arrived is named "Loading subclan", never the parent clan.
        key: "subclan-home",
        label: subclanWorkspaceLabel,
        to: buildSubclanRailPath("/"),
        icon: IconSubclans,
        end: true,
        resolved: true,
        busy: subclanPending && !`${subclanName || ""}`.trim(),
      },
      {
        key: "messages",
        label: "Messages",
        to: buildSubclanRailPath("/messages"),
        activePaths: [
          buildSubclanRailPath("/messages"),
          buildSubclanRailPath("/chat"),
          buildSubclanRailPath("/conversations"),
        ],
        icon: IconMessages,
        resolved: true,
      },
      {
        key: "calendar",
        label: "Calendar",
        to: buildSubclanRailPath("/calendar"),
        activePaths: [
          buildSubclanRailPath("/calendar"),
          buildSubclanRailPath("/events"),
        ],
        icon: IconCalendar,
        resolved: true,
      },
      {
        key: "alerts",
        label: "Alerts",
        to: buildSubclanRailPath("/alerts"),
        activePaths: [
          buildSubclanRailPath("/alerts"),
          buildSubclanRailPath("/notifications"),
        ],
        icon: IconAlerts,
        resolved: true,
      },
      {
        key: "connections",
        label: "Connections",
        to: buildSubclanRailPath("/connections"),
        // T-062 S4 — `/members` is NOT Connections; it renders the subclan's
        // own member list. Claiming it here lit the Connections rail item up
        // on a URL that shows something else.
        activePaths: [
          buildSubclanRailPath("/connections"),
          buildSubclanRailPath("/bonds"),
          buildSubclanRailPath("/requests"),
        ],
        icon: IconConnections,
        resolved: true,
      },
      {
        key: "notes",
        label: "Notes",
        to: buildSubclanRailPath("/notes"),
        activePaths: [
          buildSubclanRailPath("/notes"),
          buildSubclanRailPath("/notepad"),
        ],
        icon: IconNotes,
        resolved: true,
      },
    ],
    [buildSubclanRailPath, subclanName, subclanPending, subclanWorkspaceLabel]
  );
  const visibleRailItems = useMemo(() => {
    if (!subclanOnly) return railItems;
    return hasTenantWideRailAccess
      ? [...subclanRailItems, ...railItems]
      : subclanRailItems;
  }, [hasTenantWideRailAccess, subclanOnly, subclanRailItems]);
  const currentRailPathname = normalizeRailPathname(location.pathname);

  const hasProfilePhoto = Boolean(userProfilePhoto) && !profilePhotoErrored;

  const renderProfileAvatar = (imageClassName, fallbackClassName) => {
    if (hasProfilePhoto) {
      return (
        <img
          src={userProfilePhoto}
          alt={displayName}
          className={imageClassName}
          onError={() => setProfilePhotoErrored(true)}
        />
      );
    }

    return <span className={fallbackClassName}>{profileInitial}</span>;
  };

  const handleLogout = async () => {
    try {
      await post("/api/v1/auth/logout");
    } catch (_error) {
      // Best-effort logout.
    } finally {
      dispatch(logout());
      queryClient.clear();
      clearStoredTenantPreviewSlug();
      navigate("/login", { replace: true });
    }
  };

  const handleThemeChange = (nextTheme) => {
    if (nextTheme !== currentTheme) {
      dispatch(changeTheme(nextTheme));
    }
  };

  useEffect(() => {
    setIsProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isProfileOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsProfileOpen(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isProfileOpen]);

  return (
    <div
      className={[
        styles.workspace,
        secondaryCollapsed ? styles.workspaceSecondaryCollapsed : "",
        isReferenceShell ? styles.referenceWorkspace : "",
      ].filter(Boolean).join(" ")}
      data-testid={testId}
      data-clan-surface="workspace-shell"
      data-shell-variant={shellVariant}
      style={secondaryWidth ? { "--secondary-width": secondaryWidth } : undefined}
    >
      <aside
        className={styles.iconRail}
        aria-label="Workspace navigation"
        data-testid={`${testId}-icon-rail`}
      >
        <Link
          to={tenantPath("/account")}
          className={[styles.brandPill, isReferenceShell ? styles.referenceBrandPill : ""].filter(Boolean).join(" ")}
          aria-label={`${platformBrandName} account`}
        >
          <BrandLogo logoUrl={platformLogoUrl} brandName={platformBrandName} shellVariant={shellVariant} />
        </Link>

        {isReferenceShell ? <div className={styles.iconRailRule} aria-hidden="true" /> : null}

        <nav className={styles.iconNav} data-testid={`${testId}-primary-nav`}>
          {visibleRailItems.map((item) => {
            const Icon = item.icon;
            const referenceIconName = item.key === "subclan-home" ? "subclans" : item.key;
            const itemTo = item.resolved ? item.to : tenantPath(item.to);
            const itemActivePaths = Array.isArray(item.activePaths) ? item.activePaths : [];
            return (
              <NavLink
                key={item.key}
                to={itemTo}
                end={item.end}
                title={item.label}
                aria-label={item.label}
                aria-busy={item.busy || undefined}
                data-testid={`${testId}-nav-${item.key}`}
                className={({ isActive }) => {
                  const isSubclanPaneActive =
                    subclanOnly &&
                    itemActivePaths.some(
                      (path) => normalizeRailPathname(path) === currentRailPathname
                    );
                  const isDerivedActive =
                    isActive ||
                    isSubclanPaneActive ||
                    (!subclanOnly && item.key === "subclans" && isSubclanWorkspaceRoute);
                  return `${styles.iconLink} ${isDerivedActive ? styles.iconLinkActive : ""}`.trim();
                }}
              >
                {isReferenceShell ? <ReferenceNavIcon name={referenceIconName} /> : <Icon />}
                <span className={styles.iconLabel}>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {isReferenceShell ? <div className={styles.iconRailRule} aria-hidden="true" /> : null}

        <div className={styles.iconRailFooter} ref={profileMenuRef}>
          <button
            type="button"
            className={styles.profileRailButton}
            aria-haspopup="dialog"
            aria-expanded={isProfileOpen}
            aria-label={`Open ${currentClanName} profile menu`}
            data-testid={`${testId}-profile-menu-trigger`}
            onClick={() => setIsProfileOpen((open) => !open)}
          >
            <span className={styles.profileRailAvatarFrame}>
              {renderProfileAvatar(
                styles.profileRailAvatarImage,
                styles.profileRailAvatar
              )}
            </span>
          </button>

          {isProfileOpen ? (
            <div
              className={styles.profileDialog}
              role="dialog"
              aria-label={`${currentClanName} profile menu`}
              data-testid={`${testId}-profile-menu`}
            >
              <Link
                to={tenantPath("/account")}
                className={styles.profileDialogCard}
                data-testid={`${testId}-profile-card-link`}
              >
                <div className={styles.profileDialogAvatarWrap}>
                  {renderProfileAvatar(
                    styles.profileDialogAvatarImage,
                    styles.profileDialogAvatar
                  )}
                </div>
                <div className={styles.profileDialogNameBlock}>
                  <span className={styles.profileDialogNameLine}>{displayName}</span>
                  <span className={styles.profileDialogRole}>{profileRoleLabel}</span>
                </div>
                <span className={styles.profileDialogOpenIcon} aria-hidden="true">
                  <IconOpenExternal />
                </span>
              </Link>

              <div className={styles.profileDialogSection}>
                <Link
                  to={profileDialogPrimaryHref}
                  className={styles.profileDialogItem}
                  data-testid={`${testId}-profile-primary-link`}
                >
                  <span className={styles.profileDialogItemIcon}><IconSettings /></span>
                  <span>{profileDialogPrimaryLabel}</span>
                </Link>
                {!subclanOnly && canOpenAdministration ? (
                  <Link
                    to={tenantPath("/usage")}
                    className={styles.profileDialogItem}
                    data-testid={`${testId}-profile-usage-link`}
                  >
                    <span className={styles.profileDialogItemIcon}><IconUsage /></span>
                    <span>{currentClanName} Usage</span>
                  </Link>
                ) : null}
                {!subclanOnly ? (
                  <Link
                    to={tenantPath("/profile")}
                    className={styles.profileDialogItem}
                    data-testid={`${testId}-profile-clan-profile-link`}
                  >
                    <span className={styles.profileDialogItemIcon}><IconWorkspace /></span>
                    <span>{currentClanName} Profile</span>
                  </Link>
                ) : null}
                <Link
                  to={profileDialogSupportHref}
                  className={styles.profileDialogItem}
                  aria-label={profileDialogSupportLabel}
                  data-testid={`${testId}-profile-support-link`}
                >
                  <span className={styles.profileDialogItemIcon}><IconSupport /></span>
                  <span>{profileDialogSupportLabel}</span>
                </Link>
              </div>

              <div className={styles.profileDialogFooter}>
                <div className={styles.profileDialogAppearance}>
                  <span className={styles.profileDialogAppearanceLabel}>
                    <IconMoon />
                    <span>Appearance</span>
                  </span>
                  <span className={styles.profileThemeSegment} role="group" aria-label="Appearance">
                    <button
                      type="button"
                      className={currentTheme === "light" ? styles.profileThemeButtonActive : ""}
                      aria-pressed={currentTheme === "light"}
                      onClick={() => handleThemeChange("light")}
                    >
                      Light
                    </button>
                    <button
                      type="button"
                      className={currentTheme === "dark" ? styles.profileThemeButtonActive : ""}
                      aria-pressed={currentTheme === "dark"}
                      onClick={() => handleThemeChange("dark")}
                    >
                      Dark
                    </button>
                  </span>
                </div>
                <button type="button" className={styles.profileDialogLogoutButton} onClick={handleLogout}>
                  <IconLogout />
                  <span>Log out</span>
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </aside>

      <aside
        className={`${styles.projectRail} ${secondaryCollapsed ? styles.projectRailCollapsed : ""} ${
          secondaryVariant === "solid" ? styles.projectRailSolid : ""
        }`.trim()}
        aria-label={secondaryLabel}
        aria-hidden={secondaryCollapsed}
        data-testid={`${testId}-secondary-rail`}
      >
        {secondaryContent}
      </aside>

      <section className={styles.canvas} data-testid={`${testId}-canvas`}>
        {children}
      </section>
    </div>
  );
}
